import { Component, OnInit, OnDestroy } from '@angular/core';
import { Router, ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { Subject, takeUntil } from 'rxjs';
import { filter } from 'rxjs/operators';
import { 
  PurchaseOrder, 
  PurchaseOrderStatus, 
  PurchaseOrderPriority,
  PurchaseOrderApproval,
  ApprovalStatus
} from '../../../models/PurchaseOrder';
import {
  PurchaseOrderService,
  PurchaseRequestSimpleViewItem,
  PurchaseRequestSimpleViewResponse
} from '../../../services/purchase-order.service';
import { ErrorService } from '../../../shared/services/error.service';
import { ConfirmationService } from '../../../shared/services/confirmation.service';
import {
  AvailableBalanceResponse,
  CostCentersService
} from '../../../services/cost-centers.service';
import { AuthService } from '../../../services/auth.service';

@Component({
  selector: 'app-purchase-order-approval',
  templateUrl: './purchase-order-approval.component.html',
  styleUrls: ['./purchase-order-approval.component.css']
})
export class PurchaseOrderApprovalComponent implements OnInit, OnDestroy {
  private destroy$ = new Subject<void>();
  private baseRoute = '/purchase-orders';

  // ===== PROPRIEDADES =====
  purchaseOrder: PurchaseOrder | null = null;
  approvals: PurchaseOrderApproval[] = [];
  isLoading = false;
  error: string | null = null;
  /** Último erro da decisão do gestor (corpo da API), sempre visível na tela. */
  lastManagerDecisionApiText: string | null = null;
  purchaseOrderId: number | null = null;

  /** Itens da solicitação (sem preços — apenas produto, qtd, unidade, justificativa). */
  requestItems: PurchaseRequestSimpleViewItem[] = [];

  /** Saldo do centro de custo do solicitante — único aviso visual nesta tela. */
  costCenterBalance: AvailableBalanceResponse | null = null;
  isLoadingBalance = false;
  balanceError: string | null = null;
  
  // ===== FORMULÁRIO DE APROVAÇÃO =====
  approvalForm = {
    action: 'approve' as 'approve' | 'reject' | 'request_changes',
    comments: '',
    rejectionReason: '',
    requestedChanges: ''
  };

  // ===== ENUMS PARA TEMPLATE =====
  PurchaseOrderStatus = PurchaseOrderStatus;
  PurchaseOrderPriority = PurchaseOrderPriority;
  ApprovalStatus = ApprovalStatus;

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private location: Location,
    private purchaseOrderService: PurchaseOrderService,
    private errorService: ErrorService,
    private confirmationService: ConfirmationService,
    private costCentersService: CostCentersService,
    private authService: AuthService
  ) {}

  /**
   * Apenas o gestor designado na criação da solicitação pode aprovar/reprovar/pedir ajustes.
   * Compara o `staffId` do JWT com o `managerApproverStaffId` carregado do simple-view.
   */
  isDesignatedManager(): boolean {
    const currentStaffId = this.authService.getStaffIdFromToken();
    const requiredStaffId = this.purchaseOrder?.approvedByStaffId;
    if (!currentStaffId || currentStaffId <= 0) return false;
    if (!requiredStaffId || requiredStaffId <= 0) return false;
    return Number(currentStaffId) === Number(requiredStaffId);
  }

  /** Texto direcionado quando o usuário logado NÃO é o gestor designado. */
  get notDesignatedManagerMessage(): string {
    const managerName = (this.purchaseOrder?.approvedByStaffName || '').trim();
    if (managerName) {
      return `Apenas o gestor responsável por esta solicitação pode aprovar, reprovar ou solicitar ajustes. Gestor designado: ${managerName}.`;
    }
    return 'Apenas o gestor designado nesta solicitação pode aprovar, reprovar ou solicitar ajustes. Procure o solicitante para confirmar quem é o gestor responsável.';
  }

  get approvalPageTitle(): string {
    return this.baseRoute === '/purchase-requests'
      ? 'Aprovação de Solicitação de Compra'
      : 'Aprovação de Pedido de Compra';
  }

  ngOnInit(): void {
    this.baseRoute = this.router.url.startsWith('/purchase-requests')
      ? '/purchase-requests'
      : '/purchase-orders';
    this.route.params.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      if (!params['id']) {
        return;
      }
      const id = +params['id'];
      if (!Number.isFinite(id) || id <= 0) {
        return;
      }
      // Fluxo completo «Compras»: aprovação/rejeição não fica neste módulo.
      if (this.baseRoute === '/purchase-orders') {
        this.router.navigate(['/purchase-orders', id]);
        return;
      }
      this.purchaseOrderId = id;
      this.loadPurchaseOrder();
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  // ===== CARREGAMENTO DE DADOS =====

  /**
   * Carrega dados do pedido
   */
  loadPurchaseOrder(): void {
    if (!this.purchaseOrderId) return;

    this.isLoading = true;
    this.error = null;
    this.lastManagerDecisionApiText = null;

    this.purchaseOrderService
      .getPurchaseRequestSimpleView(this.purchaseOrderId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (simpleView: PurchaseRequestSimpleViewResponse) => {
          this.purchaseOrder = this.mapSimpleViewToPurchaseOrder(simpleView);
          this.requestItems = Array.isArray(simpleView.items) ? simpleView.items : [];
          this.approvals = [];
          this.isLoading = false;
          this.loadCostCenterBalance();
        },
        error: (error) => {
          this.error =
            this.errorService.formatHttpErrorForDisplay(error) ||
            'Erro ao carregar solicitação de compra. Tente novamente.';
          this.isLoading = false;
          console.error('Erro ao carregar solicitação:', error);
        }
      });
  }

  /**
   * Único aviso da tela: saldo disponível no centro de custo do solicitante.
   * Não exibe toast em caso de erro — usa `balanceError` no card.
   */
  private loadCostCenterBalance(): void {
    const ccId = this.purchaseOrder?.costCenterId;
    if (!ccId || ccId <= 0) {
      this.costCenterBalance = null;
      this.balanceError = null;
      return;
    }
    this.isLoadingBalance = true;
    this.balanceError = null;
    this.costCenterBalance = null;
    this.costCentersService
      .getAvailableBalance(ccId, { suppressErrorToast: true })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (resp) => {
          this.costCenterBalance = resp;
          this.isLoadingBalance = false;
        },
        error: () => {
          this.isLoadingBalance = false;
          this.balanceError =
            'Não foi possível consultar o saldo do centro de custo. Confirme manualmente a verba antes de aprovar.';
        }
      });
  }

  private mapSimpleViewToPurchaseOrder(simpleView: PurchaseRequestSimpleViewResponse): PurchaseOrder {
    return {
      id: simpleView.id,
      orderNumber: `#${simpleView.id}`,
      requestDate: new Date(simpleView.requestDate),
      status: simpleView.status || 'Solicitado',
      priority: 'Normal',
      subTotal: 0,
      totalAmount: 0,
      requestedByStaffId: simpleView.requestedByStaffId || 0,
      requestedByStaffName: simpleView.requestedByName || 'Não informado',
      departmentName: simpleView.requestedByDepartmentName,
      approvedByStaffId: simpleView.managerApproverStaffId,
      approvedByStaffName: simpleView.managerApproverName,
      costCenterId: simpleView.costCenterId,
      costCenterName: simpleView.costCenterName,
      requiresApproval: true,
      description: simpleView.description || '',
      notes: simpleView.notes || '',
      rejectionReason: simpleView.approvalSituation,
      items: [],
      approvals: [],
      daysToDelivery: 0,
      isOverdue: false,
      isPartiallyReceived: false,
      canBeApproved: false,
      canBeRejected: false,
      canBeCancelled: false,
      totalItems: simpleView.items?.length || 0,
      approvedItems: 0,
      receivedItems: 0,
      receiptPercentage: 0,
      daysInCurrentStatus: 0,
      isActive: true
    };
  }

  /**
   * Recarrega os dados
   */
  retryLoad(): void {
    this.loadPurchaseOrder();
  }

  // ===== NAVEGAÇÃO =====

  /**
   * Volta para detalhes
   */
  goBack(): void {
    this.location.back();
  }

  // ===== AÇÕES DE APROVAÇÃO =====

  /**
   * Aprova o pedido
   */
  approve(): void {
    if (!this.purchaseOrder) return;
    if (!this.ensureDesignatedManager()) return;

    this.lastManagerDecisionApiText = null;
    this.confirmationService
      .confirm({
        title: 'Confirmar aprovação',
        message: `Deseja aprovar a solicitação ${this.purchaseOrder.orderNumber}?`,
        confirmText: 'Aprovar',
        cancelText: 'Cancelar',
        type: 'question',
        icon: 'fas fa-check-circle'
      })
      .pipe(
        takeUntil(this.destroy$),
        filter((ok) => ok)
      )
      .subscribe(() => {
        const comments = (this.approvalForm.comments || '').trim() || '—';

        this.purchaseOrderService
          .submitPurchaseRequestManagerDecision(this.purchaseOrder!.id, {
            action: 'Approve',
            comments
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.errorService.showInfoMessage('Solicitação aprovada com sucesso');
              this.router.navigate([this.baseRoute, this.purchaseOrderId]);
            },
            error: (err) => {
              this.lastManagerDecisionApiText = this.errorService.formatHttpErrorForDisplay(err);
              this.errorService.handleHttpError(err);
            }
          });
      });
  }

  /**
   * Reprova a solicitação (comentário obrigatório no serviço).
   */
  reject(): void {
    if (!this.purchaseOrder) return;
    if (!this.ensureDesignatedManager()) return;

    const reason = (this.approvalForm.rejectionReason || '').trim();
    if (!reason) {
      this.errorService.showWarningMessage('Informe o motivo da reprovação.');
      return;
    }

    this.lastManagerDecisionApiText = null;
    this.confirmationService
      .confirm({
        title: 'Confirmar reprovação',
        message: `Deseja reprovar a solicitação ${this.purchaseOrder.orderNumber}?`,
        confirmText: 'Reprovar',
        cancelText: 'Cancelar',
        type: 'danger',
        icon: 'fas fa-times-circle',
        confirmButtonClass: 'btn-danger'
      })
      .pipe(
        takeUntil(this.destroy$),
        filter((ok) => ok)
      )
      .subscribe(() => {
        this.purchaseOrderService
          .submitPurchaseRequestManagerDecision(this.purchaseOrder!.id, {
            action: 'Reject',
            comments: reason
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.errorService.showInfoMessage('Solicitação reprovada');
              this.router.navigate([this.baseRoute, this.purchaseOrderId]);
            },
            error: (err) => {
              this.lastManagerDecisionApiText = this.errorService.formatHttpErrorForDisplay(err);
              this.errorService.handleHttpError(err);
            }
          });
      });
  }

  /**
   * Solicita ajustes ao solicitante (comentário obrigatório).
   */
  requestChanges(): void {
    if (!this.purchaseOrder) return;
    if (!this.ensureDesignatedManager()) return;

    const text = (this.approvalForm.requestedChanges || '').trim();
    if (!text) {
      this.errorService.showWarningMessage('Descreva o que o solicitante deve ajustar.');
      return;
    }

    this.lastManagerDecisionApiText = null;
    this.confirmationService
      .confirm({
        title: 'Confirmar pedido de ajustes',
        message: `Deseja solicitar ajustes na solicitação ${this.purchaseOrder.orderNumber}?`,
        confirmText: 'Enviar pedido',
        cancelText: 'Cancelar',
        type: 'warning',
        icon: 'fas fa-edit',
        confirmButtonClass: 'btn-warning'
      })
      .pipe(
        takeUntil(this.destroy$),
        filter((ok) => ok)
      )
      .subscribe(() => {
        this.purchaseOrderService
          .submitPurchaseRequestManagerDecision(this.purchaseOrder!.id, {
            action: 'RequestChanges',
            comments: text
          })
          .pipe(takeUntil(this.destroy$))
          .subscribe({
            next: () => {
              this.errorService.showInfoMessage('Pedido de ajuste registrado com sucesso');
              this.router.navigate([this.baseRoute, this.purchaseOrderId]);
            },
            error: (err) => {
              this.lastManagerDecisionApiText = this.errorService.formatHttpErrorForDisplay(err);
              this.errorService.handleHttpError(err);
            }
          });
      });
  }

  // ===== MÉTODOS DE VERIFICAÇÃO =====

  /**
   * Verifica se pode aprovar
   */
  canApprove(): boolean {
    if (!this.purchaseOrder) return false;
    if (!this.isDesignatedManager()) return false;
    const n = String(this.purchaseOrder.status || '').trim().toLowerCase();
    const finalized = [
      PurchaseOrderStatus.APROVADO,
      PurchaseOrderStatus.REJEITADO,
      PurchaseOrderStatus.CANCELADO,
      PurchaseOrderStatus.ENTREGUE_TOTAL
    ].map(s => s.toLowerCase());
    if (finalized.includes(n)) {
      return false;
    }
    return (
      n === PurchaseOrderStatus.SOLICITADO.toLowerCase() ||
      n === PurchaseOrderStatus.EM_APROVACAO.toLowerCase()
    );
  }

  /**
   * Garde-fou: bloqueia chamadas de aprovação/reprovação/ajustes quando o usuário
   * logado não é o gestor designado, mostrando uma mensagem direcionada.
   * Retorna `true` quando OK pra prosseguir.
   */
  private ensureDesignatedManager(): boolean {
    if (this.isDesignatedManager()) return true;
    this.lastManagerDecisionApiText = this.notDesignatedManagerMessage;
    this.errorService.showWarningMessage(this.notDesignatedManagerMessage);
    return false;
  }

  /**
   * Verifica se formulário é válido
   */
  isFormValid(): boolean {
    switch (this.approvalForm.action) {
      case 'approve':
        return true;
      case 'reject':
        return this.approvalForm.rejectionReason.trim().length > 0;
      case 'request_changes':
        return this.approvalForm.requestedChanges.trim().length > 0;
      default:
        return false;
    }
  }

  // ===== MÉTODOS DE FORMATAÇÃO =====

  /**
   * Formata valor monetário
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  }

  /**
   * Formata data
   */
  formatDate(date: Date | string | undefined): string {
    if (!date) return '';
    const dateObj = typeof date === 'string' ? new Date(date) : date;
    return dateObj.toLocaleDateString('pt-BR');
  }

  /**
   * Obtém classe CSS para status
   */
  getStatusClass(status: PurchaseOrderStatus | string): string {
    switch (status) {
      case PurchaseOrderStatus.SOLICITADO:
        return 'bg-info';
      case PurchaseOrderStatus.EM_APROVACAO:
        return 'bg-warning';
      case PurchaseOrderStatus.APROVADO:
        return 'bg-success';
      case PurchaseOrderStatus.REJEITADO:
        return 'bg-danger';
      case PurchaseOrderStatus.EM_COMPRA:
        return 'bg-primary';
      case PurchaseOrderStatus.ENTREGUE_PARCIAL:
        return 'bg-info';
      case PurchaseOrderStatus.ENTREGUE_TOTAL:
        return 'bg-success';
      case PurchaseOrderStatus.CANCELADO:
        return 'bg-secondary';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Obtém nome de exibição do status
   */
  getStatusDisplayName(status: PurchaseOrderStatus | string): string {
    switch (status) {
      case PurchaseOrderStatus.SOLICITADO:
        return 'Solicitado';
      case PurchaseOrderStatus.EM_APROVACAO:
        return 'Em Aprovação';
      case PurchaseOrderStatus.APROVADO:
        return 'Aprovado';
      case PurchaseOrderStatus.REJEITADO:
        return 'Rejeitado';
      case PurchaseOrderStatus.EM_COMPRA:
        return 'Em Compra';
      case PurchaseOrderStatus.ENTREGUE_PARCIAL:
        return 'Entregue Parcialmente';
      case PurchaseOrderStatus.ENTREGUE_TOTAL:
        return 'Entregue Totalmente';
      case PurchaseOrderStatus.CANCELADO:
        return 'Cancelado';
      default:
        return status;
    }
  }

  /**
   * Obtém classe CSS para prioridade
   */
  getPriorityClass(priority: PurchaseOrderPriority | string): string {
    switch (priority) {
      case PurchaseOrderPriority.BAIXA:
        return 'bg-secondary';
      case PurchaseOrderPriority.NORMAL:
        return 'bg-info';
      case PurchaseOrderPriority.ALTA:
        return 'bg-warning';
      case PurchaseOrderPriority.URGENTE:
        return 'bg-danger';
      default:
        return 'bg-secondary';
    }
  }

  /**
   * Obtém nome de exibição da prioridade
   */
  getPriorityDisplayName(priority: PurchaseOrderPriority | string): string {
    switch (priority) {
      case PurchaseOrderPriority.BAIXA:
        return 'Baixa';
      case PurchaseOrderPriority.NORMAL:
        return 'Normal';
      case PurchaseOrderPriority.ALTA:
        return 'Alta';
      case PurchaseOrderPriority.URGENTE:
        return 'Urgente';
      default:
        return priority;
    }
  }
}
